import {
    Key as KeyIcon,
    Security as SecurityIcon
} from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import S3Service from "../../../services/S3Service";

const CredentialsMain = () => {
    const [loading, setLoading] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadCredentials();
    }, []);

    const loadCredentials = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const creds = await S3Service.loadCredentials();
            setCredentials(creds);
        } catch (err) {
            Logger.error('Error loading AWS credentials:', err);
            setError(err.message || 'Failed to load AWS credentials');
        } finally {
            setLoading(false);
        }
    };

    const maskCredentials = (value) => {
        if (!value) return 'N/A';
        if (value.length <= 8) return value;
        return value.substring(0, 4) + '••••••••' + value.substring(value.length - 4);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!credentials) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    No AWS credentials data available.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                AWS Credentials
            </Typography>

            <Grid container spacing={3}>
                {/* Access Key */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <KeyIcon sx={{ mr: 1 }} />
                                Access Key
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Access Key ID:
                                </Typography>
                                <Typography variant="body1" sx={{ 
                                    fontWeight: 500,
                                    fontFamily: 'monospace',
                                    backgroundColor: '#f5f5f5',
                                    p: 1,
                                    borderRadius: 1,
                                    wordBreak: 'break-all'
                                }}>
                                    {maskCredentials(credentials.accessKey)}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Access Secret:
                                </Typography>
                                <Typography variant="body1" sx={{ 
                                    fontWeight: 500,
                                    fontFamily: 'monospace',
                                    backgroundColor: '#f5f5f5',
                                    p: 1,
                                    borderRadius: 1,
                                    wordBreak: 'break-all'
                                }}>
                                    {maskCredentials(credentials.accessSecret)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Security Information */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <SecurityIcon sx={{ mr: 1 }} />
                                Security Information
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Credentials Status:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                                    ✓ Loaded Successfully
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Source:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    /home/sys/credentials
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Usage:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    AWS S3 Operations
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Bucket:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    puzzleinteract-images
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Region:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    us-east-1
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Raw Credentials Data */}
                <Grid size={{ xs: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Raw Credentials Data
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ 
                                backgroundColor: '#f5f5f5',
                                p: 2,
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                maxHeight: '400px',
                                overflow: 'auto'
                            }}>
                                <pre>{JSON.stringify(credentials, null, 2)}</pre>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CredentialsMain;